import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock,
  User, MapPin, FileText, CheckCircle, AlertCircle, X,
  Stethoscope, Pill, Activity, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VisitCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch visits from backend
  const fetchVisits = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/registry/visits`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Transform backend data to component format
        const transformedVisits = data.visits.map(v => ({
          id: v.id,
          subjectId: v.subject_id,
          subjectName: v.subject_name,
          study: v.study,
          visitType: v.visit_type,
          visitNumber: v.visit_number,
          date: v.date,
          time: v.time,
          duration: v.duration,
          location: v.location,
          coordinator: v.coordinator,
          status: v.status,
          notes: v.notes,
          color: v.color || '#1877F2'
        }));
        setVisits(transformedVisits);
      }
    } catch (err) {
      console.error('Failed to fetch visits:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const handleMarkComplete = async (visitId) => {
    try {
      const response = await fetch(`${API_URL}/api/registry/visits/${visitId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Visit marked as completed');
        setSelectedVisit(null);
        fetchVisits();
      } else {
        toast.error('Failed to update visit');
      }
    } catch (err) {
      toast.error('Failed to update visit');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getVisitsForDate = (dateStr) => {
    return visits.filter(v => v.date === dateStr);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        currentMonth: false,
        date: null,
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      days.push({
        day: i,
        currentMonth: true,
        date: formatDate(date),
        isToday: formatDate(date) === formatDate(new Date()),
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        currentMonth: false,
        date: null,
      });
    }
    
    return days;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarDays = generateCalendarDays();

  // Upcoming visits (next 7 days)
  const today = formatDate(new Date());
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  const nextWeekStr = formatDate(nextWeek);
  
  const upcomingVisits = visits
    .filter(v => v.date >= today && v.date <= nextWeekStr)
    .sort((a, b) => new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time));

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Scheduled</span>;
      case 'completed':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Cancelled</span>;
      case 'no_show':
        return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">No Show</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="visit-calendar-container">
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4" data-testid="calendar-stats">
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{visits.length}</p>
                <p className="text-xs text-blue-600">Total Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{upcomingVisits.length}</p>
                <p className="text-xs text-green-600">Next 7 Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">
                  {getVisitsForDate(today).length}
                </p>
                <p className="text-xs text-orange-600">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">4</p>
                <p className="text-xs text-purple-600">Sites Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="md:col-span-2">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium text-gray-700">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={goToToday}>
                    Today
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-2">
                {dayNames.map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, idx) => {
                  const dayVisits = day.date ? getVisitsForDate(day.date) : [];
                  const isSelected = day.date && selectedDate === day.date;
                  
                  return (
                    <div
                      key={day.date || `empty-${idx}`}
                      onClick={() => day.currentMonth && setSelectedDate(day.date)}
                      className={`
                        min-h-[80px] p-1 border rounded-lg cursor-pointer transition-all
                        ${day.currentMonth ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 text-gray-400'}
                        ${day.isToday ? 'border-[#1877F2] border-2' : 'border-gray-200'}
                        ${isSelected ? 'ring-2 ring-[#1877F2] ring-offset-1' : ''}
                      `}
                    >
                      <div className={`text-sm font-medium mb-1 ${day.isToday ? 'text-[#1877F2]' : ''}`}>
                        {day.day}
                      </div>
                      <div className="space-y-1">
                        {dayVisits.slice(0, 2).map(visit => (
                          <div
                            key={visit.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVisit(visit);
                            }}
                            className="text-xs p-1 rounded truncate cursor-pointer hover:opacity-80"
                            style={{ backgroundColor: `${visit.color}20`, color: visit.color }}
                          >
                            {visit.time.split(' ')[0]} {visit.subjectName.split(' ')[0]}
                          </div>
                        ))}
                        {dayVisits.length > 2 && (
                          <div className="text-xs text-gray-500 text-center">
                            +{dayVisits.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Study Legend */}
              <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                {[...new Set(visits.map(v => v.study))].map(study => {
                  const visit = visits.find(v => v.study === study);
                  return (
                    <div key={study} className="flex items-center gap-1">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: visit?.color }}
                      ></div>
                      <span className="text-xs text-gray-600">{study}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Visits Sidebar */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium text-gray-700 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-600" />
              Upcoming Visits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingVisits.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No upcoming visits</p>
                </div>
              ) : (
                upcomingVisits.map(visit => (
                  <div
                    key={visit.id}
                    onClick={() => setSelectedVisit(visit)}
                    className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer transition-all"
                    style={{ borderLeftColor: visit.color, borderLeftWidth: '4px' }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 text-sm">{visit.subjectName}</span>
                      {getStatusBadge(visit.status)}
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{visit.visitType}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <CalendarIcon className="w-3 h-3" />
                      {visit.date}
                      <Clock className="w-3 h-3 ml-1" />
                      {visit.time}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visit Detail Modal */}
      {selectedVisit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg border-0 shadow-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{selectedVisit.visitType}</CardTitle>
                  <p className="text-sm text-gray-500">{selectedVisit.visitNumber}</p>
                </div>
                <button onClick={() => setSelectedVisit(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: `${selectedVisit.color}10`, borderLeft: `4px solid ${selectedVisit.color}` }}
              >
                <p className="font-medium text-gray-900">{selectedVisit.study}</p>
                <p className="text-sm text-gray-600">{selectedVisit.subjectName} ({selectedVisit.subjectId})</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span className="text-xs">Date & Time</span>
                  </div>
                  <p className="font-medium">{selectedVisit.date}</p>
                  <p className="text-sm text-gray-600">{selectedVisit.time} ({selectedVisit.duration})</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-xs">Location</span>
                  </div>
                  <p className="font-medium">{selectedVisit.location}</p>
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-500 mb-1">
                  <User className="w-4 h-4" />
                  <span className="text-xs">Coordinator</span>
                </div>
                <p className="font-medium">{selectedVisit.coordinator}</p>
              </div>

              {selectedVisit.notes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700 mb-1">
                    <FileText className="w-4 h-4" />
                    <span className="text-xs font-medium">Notes</span>
                  </div>
                  <p className="text-sm text-amber-800">{selectedVisit.notes}</p>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setSelectedVisit(null)}>
                  Close
                </Button>
                <Button 
                  className="bg-[#1877F2] hover:bg-[#166FE5]"
                  onClick={() => handleMarkComplete(selectedVisit.id)}
                  disabled={selectedVisit.status === 'completed'}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  {selectedVisit.status === 'completed' ? 'Completed' : 'Mark Complete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1877F2]" />
        </div>
      )}
    </div>
  );
};

export default VisitCalendar;
